import React from "react";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import PendingDataNotice from "./PendingDataNotice";

// Labels that start the positional block — divider appears before the first match.
const POSITIONAL_CATS = new Set([
    "Best QBs", "Worst QBs",
    "Best RBs", "Worst RBs",
    "Best WRs", "Worst WRs",
    "Best TEs", "Worst TEs",
    "Best Flex", "Worst Flex",
    "Best DST", "Worst DST",
    "Best K", "Worst K",
    "Best Bench", "Worst Bench",
]);

const CAT_SHORT = {
    "Most Points Scored": "Most Points",
    "Least Points Scored": "Fewest Points",
    "Best Possible Lineup": "Best Optimal Lineup",
    "Worst Optimal Lineup": "Worst Optimal Lineup",
    "Best Lineup Setter": "Lineup Setter",
    "Worst Lineup Setter": "Lineup Setter",
    "Projection Outperformer": "Proj. Outperformer",
    "Projection Underperformer": "Proj. Underperformer",
    "Fewest inactive players": "Fewest Inactive",
    "Most inactive players": "Most Inactive",
    "Best Bench": "Best Bench",
    "Worst Bench": "Worst Bench",
};

const COLORS = {
    forest: "#1f3829",
    green: "#2d6a4f",
    greenMid: "#52b788",
    greenLight: "#d8f3dc",
    coral: "#c94f3a",
    coralLight: "#fde8e5",
    mustard: "#d4a520",
    mustardLight: "#fdf3cd",
    surface: "#faf8f3",
    parchment: "#fffaf2",
    text: "#1a1a14",
    textMuted: "#6b7a5e",
    borderCard: "#c8d5c0",
    bg: "#f0ede4",
};

const styles = {
    card: {
        background: "#fff",
        border: `1px solid ${COLORS.borderCard}`,
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(31,56,41,.12)",
        overflow: "hidden",
    },
    colHeads: {
        display: "grid",
        gridTemplateColumns: "1fr 1px 1fr",
    },
    honorHead: {
        display: "flex",
        alignItems: "center",
        gap: "0.65rem",
        padding: "0.72rem 1.1rem",
        background: `linear-gradient(180deg, ${COLORS.forest}, #172b20)`,
        borderRadius: "7px 0 0 0",
    },
    roughHead: {
        display: "flex",
        alignItems: "center",
        gap: "0.65rem",
        padding: "0.72rem 1.1rem",
        background: `linear-gradient(180deg, ${COLORS.forest}, #172b20)`,
        borderRadius: "0 7px 0 0",
    },
    headLabel: (color) => ({
        fontSize: "0.65rem",
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        color,
    }),
    headRule: {
        flex: 1,
        height: 1,
        background: "rgba(255, 250, 242, 0.22)",
    },
    divider: {
        background: COLORS.borderCard,
    },
    // Section break between the seven core awards and the positional block.
    sectionDivider: {
        height: 2,
        background: COLORS.mustard,
        margin: "0.4rem 0",
    },
    row: (index) => ({
        display: "grid",
        gridTemplateColumns: "1fr 1px 1fr",
        borderBottom: "1px solid #f4f1e8",
        background: index % 2 === 0 ? "#fff" : "#fdfcf8",
    }),
    cell: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        alignItems: "center",
        padding: "0.58rem 0.75rem",
        minWidth: 0,
    },
    cat: {
        fontSize: "0.66rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: COLORS.textMuted,
        textAlign: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    owner: {
        fontSize: "0.9rem",
        fontWeight: 800,
        color: COLORS.text,
        textAlign: "center",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    valueGroup: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.35rem",
        minWidth: 0,
    },
    val: {
        fontFamily: "'Courier Prime', 'Courier New', monospace",
        fontSize: "0.78rem",
        fontWeight: 700,
        color: COLORS.textMuted,
        textAlign: "center",
        whiteSpace: "nowrap",
    },
    rec: {
        display: "inline-block",
        fontSize: "0.52rem",
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: "#7a5c0a",
        background: COLORS.mustardLight,
        border: `1px solid ${COLORS.mustard}`,
        padding: "0.1rem 0.38rem",
        borderRadius: 4,
        flexShrink: 0,
    },
    emptyCell: {
        padding: "2rem",
        textAlign: "center",
        color: COLORS.textMuted,
        fontSize: "0.9rem",
        gridColumn: "1 / -1",
    },
};

function AwardCell({ award, sectionLabel }) {
    const [cat, owner, val, isRecord] = award;
    const cleanCat = cat.replace(/:\s*$/, "").trim();
    const displayCat = CAT_SHORT[cleanCat] || cleanCat;
    return (
        <div className="weekly-awards-cell" style={styles.cell}>
            <span className="weekly-awards-mobile-section">{sectionLabel}</span>
            <span className="weekly-awards-category" style={styles.cat}>{displayCat}</span>
            <span className="weekly-awards-owner" style={styles.owner}>{owner}</span>
            <div className="weekly-awards-value-group" style={styles.valueGroup}>
                <span className="weekly-awards-value" style={styles.val}>{val}</span>
                {isRecord && <span className="weekly-awards-record" style={styles.rec}>Rec</span>}
            </div>
        </div>
    );
}

const WeeklyAwardsTable = ({ data, week, weekLabel, loading, nCompletedWeeks }) => {
    const label = weekLabel || data?.weekLabel || `Week ${week}`;

    const renderBody = () => {
        if (loading || !data) {
            return (
                <div style={{ padding: "2rem", textAlign: "center", color: COLORS.textMuted }}>
                    Loading Weekly Awards...
                </div>
            );
        }
        if (!data.bestAwards || data.bestAwards.length === 0) {
            return (
                <div style={styles.emptyCell}>No weekly awards available.</div>
            );
        }
        let dividerEmitted = false;
        return data.bestAwards.map((best, i) => {
            const worst = data.worstAwards[i];
            const cleanCat = (best[0] || "").replace(/:\s*$/, "").trim();
            const displayCat = CAT_SHORT[cleanCat] || cleanCat;
            const isPositional = POSITIONAL_CATS.has(displayCat);
            const showDivider = isPositional && !dividerEmitted;
            if (isPositional) dividerEmitted = true;
            const isLast = i === data.bestAwards.length - 1;
            return (
                <React.Fragment key={i}>
                    {showDivider && <div style={styles.sectionDivider} />}
                    <div className="weekly-awards-row" style={{ ...styles.row(i), ...(isLast ? { borderBottom: "none" } : {}) }}>
                        <AwardCell award={best} sectionLabel="Honors" />
                        <div className="weekly-awards-divider" style={styles.divider} />
                        {worst && <AwardCell award={worst} sectionLabel="Rough Week" />}
                    </div>
                </React.Fragment>
            );
        });
    };

    return (
        <CopyableContainer
            title={
                <LeagueTabHeading
                    eyebrow="Weekly Awards"
                    title={label}
                    className="mb-0"
                />
            }
            fileName={`weekly-awards-week-${week}`}
            exportWidth={720}
        >
            <PendingDataNotice
                dataType="award winners"
                isPending={week > nCompletedWeeks}
            />
            <div className="weekly-awards-scroll-shell">
                <div className="weekly-awards-card" style={styles.card}>
                    <div className="weekly-awards-heads" style={styles.colHeads}>
                        <div className="weekly-awards-head weekly-awards-head-honors" style={styles.honorHead}>
                            <span style={styles.headLabel(COLORS.parchment)}>Honors</span>
                            <div style={styles.headRule} />
                        </div>
                        <div className="weekly-awards-divider" style={styles.divider} />
                        <div className="weekly-awards-head weekly-awards-head-rough" style={styles.roughHead}>
                            <span style={styles.headLabel(COLORS.parchment)}>Rough Week</span>
                            <div style={styles.headRule} />
                        </div>
                    </div>
                    {renderBody()}
                </div>
            </div>
        </CopyableContainer>
    );
};

export default WeeklyAwardsTable;
